import { createMemo } from 'solid-js';
import { createQuery, type CreateQueryResult } from '@tanstack/solid-query';
import { sportsApi } from '~/shared/api/sports-api';
import type { SportDto } from '~/shared/api/generated';
import { queryKeys } from '~/shared/api/query-keys';
import { isSportOpen, isSportShown } from './sport-state';

export interface UseSports {
  query: CreateQueryResult<{ items: SportDto[] }, Error>;
  items: () => SportDto[];
  openSorted: (keep?: string | null) => SportDto[];
  shownSorted: () => SportDto[];
  byCode: (code: string) => SportDto | undefined;
  labelOf: (code: string, fallback?: string) => string;
  colorOf: (code: string, fallback?: string) => string | undefined;
  iconOf: (code: string, fallback?: string) => string;
}

export function useSports(): UseSports {
  const query = createQuery(() => ({
    queryKey: queryKeys.sports.all(),
    queryFn: ({ signal }) => sportsApi.list({ signal }),
    staleTime: 60_000,
  }));

  const items = createMemo<SportDto[]>(() => query.data?.items ?? []);

  const sorted = createMemo<SportDto[]>(() => [...items()].sort((a, b) => a.sortOrder - b.sortOrder));
  const openSorted = (keep?: string | null): SportDto[] =>
    sorted().filter((s) => isSportOpen(s) || (keep != null && s.code.toLowerCase() === keep.toLowerCase()));
  const shownSorted = createMemo<SportDto[]>(() => sorted().filter(isSportShown));

  const indexByCode = createMemo<Map<string, SportDto>>(() => {
    const m = new Map<string, SportDto>();
    for (const s of items()) m.set(s.code.toLowerCase(), s);
    return m;
  });

  const byCode = (code?: string | null): SportDto | undefined =>
    code ? indexByCode().get(code.toLowerCase()) : undefined;

  const labelOf = (code?: string | null, fallback?: string): string =>
    byCode(code)?.label ?? fallback ?? code ?? '';

  const colorOf = (code?: string | null, fallback?: string): string | undefined =>
    byCode(code)?.color ?? fallback;

  const iconOf = (code?: string | null, fallback?: string): string =>
    byCode(code)?.icon ?? fallback ?? 'Sparkles';

  return {
    query,
    items,
    openSorted,
    shownSorted,
    byCode,
    labelOf,
    colorOf,
    iconOf,
  };
}
